import { graphql } from '~/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { graphqlRequest } from '~/lib/graphql-client';
import type {
  PaginatedFetchOptions,
  RouteSort,
} from '~/hooks/usePaginatedQuery';
import type { FacilityScope, LocationScope } from '~/graphql/graphql';

const suggestAddressesQuery = graphql(`
  query SuggestAddresses($query: String!) {
    suggestAddresses(query: $query) {
      text
      placeId
    }
  }
`);

const resolveAddressQuery = graphql(`
  query ResolveAddress($text: String!) {
    resolveAddress(text: $text) {
      addressLine1
      city
      region
      countryCode
      zipCode
      latitude
      longitude
    }
  }
`);

export const resolveAddress = (text: string) =>
  graphqlRequest(resolveAddressQuery, { text });

const reverseGeocodeQuery = graphql(`
  query ReverseGeocode($latitude: Float!, $longitude: Float!) {
    reverseGeocode(latitude: $latitude, longitude: $longitude) {
      addressLine1
      city
      region
      countryCode
      zipCode
      latitude
      longitude
    }
  }
`);

export const reverseGeocode = (latitude: number, longitude: number) =>
  graphqlRequest(reverseGeocodeQuery, { latitude, longitude });

export const useSuggestAddresses = (query: string) => {
  return useGraphqlQuery(
    suggestAddressesQuery,
    ['suggestAddresses', query],
    { query },
    { enabled: query.trim().length >= 2, staleTime: 10_000 }
  );
};

const getOrganizationFacilitiesPageQuery = graphql(`
  query GetOrganizationFacilitiesPage(
    $page: Int!
    $size: Int!
    $search: String
    $scope: FacilityScope
  ) {
    organizationFacilitiesPage(
      pageInput: { page: $page, size: $size }
      filters: { search: $search, scope: $scope }
    ) {
      content {
        id
        code
        phoneNumber
        faxNumber
        address {
          addressLine1
          addressLine2
          city
          region
          zipCode
          country
        }
        transportVehicles {
          id
          name
          vehicleType
        }
        locations {
          id
          name
          code
          branchCode
        }
        shippingRates {
          id
          shippingRate {
            id
            zone
            distanceInMileMin
            distanceInMileMax
            shippingDaysMin
            shippingDaysMax
          }
          ratePerMbf
          rateCurrency
        }
        nearestPort {
          id
          code
          name
          city
          country
        }
      }
      paging {
        totalElements
        totalPages
        number
        size
      }
    }
  }
`);

const getOrganizationFacilitiesQuery = graphql(`
  query GetOrganizationFacilities {
    organizationFacilities {
      id
      code
      address {
        city
      }
      locations {
        branchCode
      }
    }
  }
`);

const getFacilityAddressOptionsQuery = graphql(`
  query GetFacilityAddressOptions {
    organizationFacilities {
      id
      code
      address {
        addressLine1
        addressLine2
        city
        region
        zipCode
      }
    }
  }
`);

export const useGetFacilityAddressOptions = (options?: {
  enabled?: boolean;
}) => {
  return useGraphqlQuery(
    getFacilityAddressOptionsQuery,
    ['facilityAddressOptions'],
    undefined,
    { enabled: options?.enabled ?? true }
  );
};

const getOrganizationLocationsPageQuery = graphql(`
  query GetOrganizationLocationsPage(
    $page: Int!
    $size: Int!
    $search: String
    $scope: LocationScope
  ) {
    organizationLocationsPage(
      pageInput: { page: $page, size: $size }
      filters: { search: $search, scope: $scope }
    ) {
      content {
        id
        code
        name
        branchCode
        facility {
          id
          code
          address {
            addressLine1
            addressLine2
            city
            region
            zipCode
            country
          }
        }
      }
      paging {
        totalElements
        totalPages
        number
        size
      }
    }
  }
`);

export type OrganizationLocationsFilters = {
  search?: string;
  scope?: LocationScope;
};

export const useGetOrganizationLocationsPage = (
  page: number,
  size: number,
  filters?: OrganizationLocationsFilters,
  _sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  return useGraphqlQuery(
    getOrganizationLocationsPageQuery,
    [
      'organizationLocationsPage',
      page.toString(),
      size.toString(),
      filters ?? null,
    ],
    {
      page,
      size,
      search: filters?.search ?? null,
      scope: filters?.scope ?? null,
    },
    options
  );
};

export const useGetOrganizationFacilities = () => {
  return useGraphqlQuery(getOrganizationFacilitiesQuery, [
    'organizationFacilities',
  ]);
};

export type OrganizationFacilitiesFilters = {
  search?: string;
  scope?: FacilityScope;
};

export const useGetOrganizationFacilitiesPage = (
  page: number,
  size: number,
  filters?: OrganizationFacilitiesFilters,
  _sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  return useGraphqlQuery(
    getOrganizationFacilitiesPageQuery,
    [
      'organizationFacilitiesPage',
      page.toString(),
      size.toString(),
      filters ?? null,
    ],
    {
      page,
      size,
      search: filters?.search ?? null,
      scope: filters?.scope ?? null,
    },
    options
  );
};

export const useGetTransportVehicles = () => {
  const getTransportVehiclesQuery = graphql(`
    query GetTransportVehicles {
      transportVehicles {
        id
        name
        vehicleType
      }
    }
  `);
  return useGraphqlQuery(getTransportVehiclesQuery, ['transportVehicles']);
};

export const useGetTransportVehicleById = (id: string) => {
  const query = graphql(`
    query GetTransportVehicleById($id: ID!) {
      transportVehicle(id: $id) {
        id
        name
        vehicleType
      }
    }
  `);
  return useGraphqlQuery(
    query,
    ['transportVehicle', id],
    { id },
    { enabled: !!id }
  );
};

export const useAddTransportVehiclesToFacility = () => {
  const addTransportVehiclesToFacilityMutation = graphql(`
    mutation AddTransportVehiclesToFacility(
      $facilityId: ID!
      $transportVehicleIds: [ID!]!
    ) {
      addTransportVehiclesToFacility(
        facilityId: $facilityId
        transportVehicleIds: $transportVehicleIds
      ) {
        id
        transportVehicles {
          id
          name
          vehicleType
        }
      }
    }
  `);
  return useGraphqlMutation(addTransportVehiclesToFacilityMutation, [
    'organizationFacilitiesPage',
  ]);
};

export const useUpsertFacility = () => {
  const upsertFacilityMutation = graphql(`
    mutation UpsertFacility($id: ID, $input: FacilityInput!) {
      upsertFacility(id: $id, input: $input) {
        id
        code
        phoneNumber
        faxNumber
        address {
          addressLine1
          addressLine2
          city
          region
          zipCode
          country
        }
        nearestPort {
          id
          code
          name
          city
          country
        }
      }
    }
  `);
  return useGraphqlMutation(upsertFacilityMutation, [
    'organizationFacilitiesPage',
    'organizationLocationsPage',
    'organizationFacilities',
    'facilityAddressOptions',
  ]);
};

export const useDeleteFacility = () => {
  const deleteFacilityMutation = graphql(`
    mutation DeleteFacility($id: ID!) {
      deleteFacility(id: $id)
    }
  `);
  return useGraphqlMutation(deleteFacilityMutation, [
    'organizationFacilitiesPage',
    'organizationLocationsPage',
    'organizationFacilities',
    'facilityAddressOptions',
  ]);
};

export const useRemoveTransportVehiclesFromFacility = () => {
  const removeTransportVehiclesFromFacilityMutation = graphql(`
    mutation RemoveTransportVehiclesFromFacility(
      $facilityId: ID!
      $transportVehicleIds: [ID!]!
    ) {
      removeTransportVehiclesFromFacility(
        facilityId: $facilityId
        transportVehicleIds: $transportVehicleIds
      ) {
        id
        transportVehicles {
          id
          name
          vehicleType
        }
      }
    }
  `);
  return useGraphqlMutation(removeTransportVehiclesFromFacilityMutation, [
    'organizationFacilitiesPage',
  ]);
};

export const useGetShippingRates = () => {
  const getShippingRatesQuery = graphql(`
    query GetShippingRates {
      shippingRates {
        id
        zone
        distanceInMileMin
        distanceInMileMax
        shippingDaysMin
        shippingDaysMax
      }
    }
  `);
  return useGraphqlQuery(getShippingRatesQuery, ['shippingRates'], undefined, {
    staleTime: Infinity,
  });
};

export const useUpsertFacilityShippingRates = () => {
  const upsertFacilityShippingRatesMutation = graphql(`
    mutation UpsertFacilityShippingRates(
      $facilityId: ID!
      $shippingRates: [ShippingRateInput!]!
    ) {
      upsertFacilityShippingRates(
        facilityId: $facilityId
        shippingRates: $shippingRates
      ) {
        id
      }
    }
  `);
  return useGraphqlMutation(upsertFacilityShippingRatesMutation, [
    'organizationFacilitiesPage',
  ]);
};
